import { VariableCardProps } from './VariableCard.tsx';
import { AppMenuItem } from 'components/uikit/menu/AppMenu.tsx';
import styles from './VariableCard.module.scss';
import { useCallback, useMemo } from 'react';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import {
  SystemVariableType,
  UrlType,
  VariableLockStatusDto,
} from 'services/api/api-client.types.ts';
import { useModal } from 'components/uikit/modal/useModal.tsx';
import { VariableDocumentList } from '../variableDocumentsList/VariableDocumentsList.tsx';
import i18next from 'i18next';
import { Trans } from 'react-i18next';
import { showSuccessToast } from 'components/uikit/toast/showToast.tsx';
import clsx from 'clsx';

type useVariableCardAppMenuItemsProps = VariableCardProps & {
  onPreviewUpdate: (postfix?: string) => void;
  onDelete: () => void;
  onMerge: () => void;
  variableManageAllowed: boolean;
  isLockedByOtherEditor: boolean;
};

export const useVariableCardAppMenuItems = (
  props: useVariableCardAppMenuItemsProps,
) => {
  const i18nMenu = useScopedTranslation('Page.Variables.MenuItems');
  const i18nSoup = useScopedTranslation('Page.DocumentEditor.Variables.Soup');
  const i18nVariables = useScopedTranslation('Page.Variables');
  const modals = useModal();

  const onPreviewUpdates = async () => {
    let postfix;
    if (props.variable.actualValue.urlType === UrlType.Soup) {
      const result = await modals.showMultiButton({
        buttons: [
          {
            id: 'SOUP',
            text: i18nSoup.t('select_soup'),
          },
          {
            id: 'SAD',
            text: i18nSoup.t('select_sad'),
          },
        ],
        title: i18nSoup.t('title'),
        text: i18nSoup.t('text'),
      });
      if (result) postfix = result;
    }

    props.onPreviewUpdate(postfix);
  };

  const variableUsedInCurrentRevision = props.variable.documents.filter(
    (x) => x.revisionId === props.revisionId,
  );

  const deleteAlert = useCallback(
    () =>
      modals.showAlert({
        title: i18nVariables.t('delete_alert_title'),
        text: (
          <div>
            {i18nVariables.t('delete_alert_text')}
            <VariableDocumentList documents={variableUsedInCurrentRevision} />
          </div>
        ),
      }),
    [props.variable.documents],
  );

  const viewUsageModal = useCallback(
    () =>
      modals.showAlert({
        title: i18nVariables.t('ViewUsageModal.title'),
        text: (
          <div>
            <Trans
              i18nKey={'Page.Variables.ViewUsageModal.text'}
              values={{
                variableName: props.variable.name,
                variableId: props.variable.id,
                documentsCount: props.variable.documents.length,
              }}
              components={{
                bold: <b />,
                grey: <span className={styles.grey} />,
              }}
            />
            <VariableDocumentList
              documents={props.variable.documents}
              currentRevisionId={props.revisionId}
            />
          </div>
        ),
      }),
    [props.variable.documents, props.variable.name, props.variable.id],
  );

  const onCopyLink = useCallback(async () => {
    if (!props.variable.actualUrl) return;

    await navigator.clipboard.writeText(props.variable.actualUrl);
    showSuccessToast({
      content: {
        title: i18nMenu.t('copy_link_toast_title'),
        description: (
          <Trans
            i18nKey="Page.Variables.MenuItems.copy_link_toast_description"
            components={{
              bold: <b />,
              grey: <span className={styles.grey} />,
            }}
            values={{
              variableName: props.variable.name,
              variableId: props.variable.id,
            }}
          />
        ),
      },
      options: { autoClose: 2000 },
    });
  }, [
    props.variable.actualUrl,
    props.variable.name,
    props.variable.id,
    i18nMenu,
  ]);

  const isTable = props.variable.isTable;
  const isSystemVariable = props.variable.systemVariableType !== null;
  const isAutoVariable = props.variable.actualUrl !== null;

  return useMemo(
    (): AppMenuItem[] => [
      ...(props.variable.systemVariableType !==
        SystemVariableType.VersionHistory && isTable
        ? [
            {
              key: 'preview_updates',
              text: i18nMenu.t('preview_updates'),
              onClick: onPreviewUpdates,
            },
          ]
        : []),

      ...(!isSystemVariable
        ? [
            {
              key: 'merge',
              text: i18nMenu.t('merge'),
              onClick: () => {
                if (props.isReadOnly || props.isLockedByOtherEditor) return;
                props.onMerge();
              },
              hidden:
                !props.variableManageAllowed || props.isLockedByOtherEditor,
            },
          ]
        : []),

      ...(isTable
        ? [
            {
              key: 'csv_export',
              text: i18nMenu.t('csv_export'),
              onClick: () => {
                props.onVariableExport(props.variable.id, props.revisionId);
              },
            },
          ]
        : []),

      {
        key: 'view_usage',
        text: i18nMenu.t('view_usage'),
        onClick: viewUsageModal,
        className: styles.dividerTop,
      },

      ...(isAutoVariable
        ? [
            {
              key: 'copy_link',
              text: i18nMenu.t('copy_link'),
              onClick: onCopyLink,
            },
          ]
        : []),

      ...(!isSystemVariable
        ? [
            {
              key: 'delete',
              text: i18nMenu.t('delete'),
              className: clsx(styles.delete, styles.dividerTop),
              onClick:
                variableUsedInCurrentRevision.length > 0
                  ? () => deleteAlert()
                  : () => props.onDelete(),
              hidden:
                !props.variableManageAllowed || props.isLockedByOtherEditor,
            },
          ]
        : []),
    ],
    [i18nMenu, props, props.variableManageAllowed],
  );
};

export type VariableLockInfo = {
  lockStatuses: VariableLockStatusDto[];
  reason: string | null;
};

export function getVariableLockInfo(
  lockStatuses: VariableLockStatusDto[] | undefined,
  currentDocumentId?: number,
): VariableLockInfo {
  const lockedByOtherEditor = (lockStatuses ?? []).filter(
    (ls) => ls.lockFromDocumentId !== currentDocumentId,
  );

  return {
    lockStatuses: lockedByOtherEditor,
    reason:
      lockedByOtherEditor.length === 0
        ? null
        : getLockedVariableMessage(lockedByOtherEditor),
  };
}

export function getLockedVariableMessage(
  lockStatuses: VariableLockStatusDto[],
): string {
  const usernames = [
    ...new Set(lockStatuses.map((lockStatus) => lockStatus.lockedByUserName)),
  ];

  if (usernames.length === 1)
    return i18next.t('Page.Variables.locked_by_editor_tooltip', {
      name: usernames[0],
    });
  else if (usernames.length === 2)
    return i18next.t('Page.Variables.locked_by_editor_tooltip_two', {
      name: usernames[0],
      name2: usernames[1],
    });
  else if (usernames.length > 2)
    return i18next.t('Page.Variables.locked_by_editor_tooltip_multiple', {
      name: usernames[0],
      othersCount: usernames.length - 1,
    });

  throw new Error(
    'Invalid number of names provided for localization: ' + usernames.length,
  );
}
